import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { useAuth } from '../app/auth-context'
import { Badge, ORDER_TONE, POLICY_TONE } from '../components/badge'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import {
  customerName,
  getCustomer,
  suspendCustomer,
  unsuspendCustomer,
  type CustomerDetailDto,
  type CustomerProfileDto,
} from '../lib/customers-api'
import type { StaffRole } from '../lib/staff-api'
import {
  formatJalaliDate,
  formatJalaliDateTime,
  formatMobile,
  formatToman,
  toPersianDigits,
} from '../lib/fa'

const messageOf = (error: unknown): string =>
  error instanceof ApiError
    ? error.code === 'NOT_FOUND'
      ? 'این مشتری پیدا نشد.'
      : error.messageFa
    : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/** Mirrors `CUSTOMER_SUSPENSION_ROLES` in apps/api/src/backoffice/staff.constants.ts. */
const SUSPENSION_ROLES: StaffRole[] = ['ADMIN']

/** Someone else got there first; the page is stale, not the request wrong. */
const STALE_CODES = new Set(['CUSTOMER_ALREADY_SUSPENDED', 'CUSTOMER_NOT_SUSPENDED'])

/** One customer, everything about them. The only thing that changes anything is suspension. */
export function CustomerDetailPage() {
  const { id = '' } = useParams()
  const location = useLocation()
  const customer = useQuery({ queryKey: ['customer', id], queryFn: () => getCustomer(id) })

  // Back to the same list, search and page, if that is where we came from.
  const backTo = (location.state as { from?: string } | null)?.from ?? '/customers'
  const backLabel = backTo.startsWith('/orders')
    ? 'سفارش‌ها'
    : backTo.startsWith('/payments')
      ? 'پرداخت‌ها'
      : 'مشتریان'

  return (
    <div className="max-w-6xl">
      <Link to={backTo} className="mb-4 inline-block text-sm font-semibold text-brand-600">
        → {backLabel}
      </Link>

      {customer.isPending ? (
        <p className="text-sm text-muted">در حال بارگذاری…</p>
      ) : customer.isError ? (
        <ErrorNote>{messageOf(customer.error)}</ErrorNote>
      ) : (
        <CustomerDetail data={customer.data} />
      )}
    </div>
  )
}

function CustomerDetail({ data }: { data: CustomerDetailDto }) {
  const location = useLocation()
  const from = `${location.pathname}${location.search}`
  const { profile, counts, recent } = data
  const name = customerName(profile)

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">{name ?? 'مشتری بدون نام'}</h1>
        {profile.suspendedAt ? <Badge tone="bad">مسدود</Badge> : null}
        <SuspensionActions profile={profile} />
      </div>

      {profile.suspendedAt ? (
        <div
          role="status"
          className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          <p className="font-semibold">
            حساب این مشتری از {formatJalaliDateTime(profile.suspendedAt)} مسدود است.
          </p>
          <p className="mt-1">نمی‌تواند وارد شود و نشست‌های بازش بسته شده‌اند.</p>
          {profile.suspendedReason ? (
            <p className="mt-3 whitespace-pre-line">
              <span className="font-semibold">دلیل: </span>
              {profile.suspendedReason}
            </p>
          ) : null}
        </div>
      ) : null}

      <Card>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm lg:grid-cols-4">
          <Field label="موبایل">
            <span dir="ltr">{formatMobile(profile.mobile)}</span>
          </Field>
          <Field label="کد ملی">
            {profile.nationalCode ? toPersianDigits(profile.nationalCode) : '—'}
          </Field>
          <Field label="تاریخ تولد">
            {profile.birthDate ? formatJalaliDate(profile.birthDate) : '—'}
          </Field>
          <Field label="ایمیل">
            {profile.email ? (
              <span dir="ltr" className="break-all">
                {profile.email}
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label="پروفایل">{profile.isProfileComplete ? 'کامل' : 'ناقص'}</Field>
          <Field label="عضویت">{formatJalaliDateTime(profile.createdAt)}</Field>
          <Field label="آخرین تغییر پروفایل">{formatJalaliDateTime(profile.updatedAt)}</Field>
        </dl>
      </Card>

      <Section
        title="بیمه‌نامه‌ها"
        shown={recent.policies.length}
        total={counts.policies}
        empty="بیمه‌نامه‌ای صادر نشده است."
      >
        <Table head={['شماره', 'محصول', 'بیمه‌گر', 'اعتبار', 'مبلغ', 'وضعیت']}>
          {recent.policies.map((policy) => (
            <tr key={policy.id} className="border-b border-line last:border-0">
              <td className="px-4 py-3 font-medium" dir="ltr" style={{ textAlign: 'right' }}>
                {toPersianDigits(policy.policyNumber)}
              </td>
              <td className="px-4 py-3">{policy.productTitleFa}</td>
              <td className="px-4 py-3">{policy.insurerName}</td>
              <td className="whitespace-nowrap px-4 py-3 text-muted">
                {formatJalaliDate(policy.startsAt)} تا {formatJalaliDate(policy.endsAt)}
              </td>
              <td className="whitespace-nowrap px-4 py-3">{formatToman(policy.amount)}</td>
              <td className="px-4 py-3">
                <Badge tone={POLICY_TONE[policy.status]}>{policy.statusFa}</Badge>
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="سفارش‌ها"
        shown={recent.orders.length}
        total={counts.orders}
        empty="سفارشی ثبت نشده است."
      >
        <Table head={['زمان', 'محصول', 'بیمه‌گر', 'مبلغ', 'وضعیت', 'شناسه']}>
          {recent.orders.map((order) => (
            <tr key={order.id} className="border-b border-line last:border-0">
              <td className="whitespace-nowrap px-4 py-3 text-muted">
                {formatJalaliDateTime(order.createdAt)}
              </td>
              <td className="px-4 py-3">
                <Link
                  to={`/orders/${order.id}`}
                  state={{ from }}
                  className="font-medium hover:text-brand-600"
                >
                  {order.productTitleFa}
                </Link>
              </td>
              <td className="px-4 py-3">{order.insurerName}</td>
              <td className="whitespace-nowrap px-4 py-3">{formatToman(order.amount)}</td>
              <td className="px-4 py-3">
                <Badge tone={ORDER_TONE[order.status]}>{order.statusFa}</Badge>
              </td>
              <td className="px-4 py-3 text-xs text-muted" dir="ltr" style={{ textAlign: 'right' }}>
                <Link to={`/orders/${order.id}`} state={{ from }} className="hover:text-brand-600">
                  {order.id}
                </Link>
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="خودروها"
        shown={recent.vehicles.length}
        total={counts.vehicles}
        empty="خودرویی ثبت نشده است."
      >
        <Table head={['خودرو', 'پلاک', 'سال ساخت', 'نوع', 'کاربری', 'ثبت']}>
          {recent.vehicles.map((vehicle) => (
            <tr key={vehicle.id} className="border-b border-line last:border-0">
              <td className="px-4 py-3 font-medium">{vehicle.modelLabelFa}</td>
              <td className="whitespace-nowrap px-4 py-3">{vehicle.plateFa}</td>
              <td className="px-4 py-3">{toPersianDigits(vehicle.productionYear)}</td>
              <td className="px-4 py-3">{vehicle.groupFa}</td>
              <td className="px-4 py-3">{vehicle.usageFa}</td>
              <td className="whitespace-nowrap px-4 py-3 text-muted">
                {formatJalaliDate(vehicle.createdAt)}
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section
        title="بیمه‌شدگان"
        shown={recent.insuredPersons.length}
        total={counts.insuredPersons}
        empty="بیمه‌شده‌ای ثبت نشده است."
      >
        <Table head={['نام', 'کد ملی', 'تاریخ تولد', 'گذرنامه', 'ثبت']}>
          {recent.insuredPersons.map((person) => (
            <tr key={person.id} className="border-b border-line last:border-0">
              <td className="px-4 py-3 font-medium">
                {person.firstName} {person.lastName}
              </td>
              <td className="px-4 py-3">{toPersianDigits(person.nationalCode)}</td>
              <td className="whitespace-nowrap px-4 py-3">{formatJalaliDate(person.birthDate)}</td>
              <td className="px-4 py-3" dir="ltr" style={{ textAlign: 'right' }}>
                {person.passportNo ?? '—'}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-muted">
                {formatJalaliDate(person.createdAt)}
              </td>
            </tr>
          ))}
        </Table>
      </Section>
    </>
  )
}

/**
 * Suspend / unsuspend, for the roles the API allows; everyone else sees no buttons. A 409 means
 * another admin acted first, so the page reloads the customer and says so.
 */
function SuspensionActions({ profile }: { profile: CustomerProfileDto }) {
  const { staff } = useAuth()
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  /** Shown beside the buttons: a stale 409, or an unsuspend that failed. */
  const [notice, setNotice] = useState<string | null>(null)

  const refresh = (next?: CustomerProfileDto) => {
    if (next) {
      queryClient.setQueryData<CustomerDetailDto>(['customer', profile.id], (old) =>
        old ? { ...old, profile: next } : old,
      )
    }
    void queryClient.invalidateQueries({ queryKey: ['customer', profile.id] })
    void queryClient.invalidateQueries({ queryKey: ['customers'] })
    void queryClient.invalidateQueries({ queryKey: ['audit'] })
  }
  const isStale = (error: Error) => error instanceof ApiError && STALE_CODES.has(error.code)

  const suspend = useMutation({
    mutationFn: (reason: string) => suspendCustomer(profile.id, reason),
    onSuccess: (next) => {
      setDialogOpen(false)
      refresh(next)
    },
    onError: (error) => {
      // Anything else stays in the dialog, next to the reason the admin may want to fix.
      if (!isStale(error)) return
      setDialogOpen(false)
      setNotice(`${messageOf(error)} صفحه به‌روز شد.`)
      refresh()
    },
  })
  const unsuspend = useMutation({
    mutationFn: () => unsuspendCustomer(profile.id),
    onSuccess: (next) => refresh(next),
    onError: (error) => {
      if (!isStale(error)) return setNotice(messageOf(error))
      setNotice(`${messageOf(error)} صفحه به‌روز شد.`)
      refresh()
    },
  })

  if (!staff || !SUSPENSION_ROLES.includes(staff.role)) return null

  return (
    <div className="ms-auto flex items-center gap-3">
      {notice ? <ErrorNote>{notice}</ErrorNote> : null}
      {profile.suspendedAt ? (
        <Button
          variant="secondary"
          loading={unsuspend.isPending}
          onClick={() => {
            setNotice(null)
            if (window.confirm('مسدودی این مشتری برداشته شود؟ برای ورود باید دوباره کد بگیرد.')) {
              unsuspend.mutate()
            }
          }}
        >
          برداشتن مسدودی
        </Button>
      ) : (
        <Button
          variant="danger"
          onClick={() => {
            setNotice(null)
            suspend.reset()
            setDialogOpen(true)
          }}
        >
          مسدودکردن
        </Button>
      )}
      {dialogOpen ? (
        <SuspendDialog
          pending={suspend.isPending}
          error={suspend.error}
          onSubmit={(reason) => suspend.mutate(reason)}
          onClose={() => setDialogOpen(false)}
        />
      ) : null}
    </div>
  )
}

const REASON_MAX = 500

function SuspendDialog({
  pending,
  error,
  onSubmit,
  onClose,
}: {
  pending: boolean
  error: Error | null
  onSubmit: (reason: string) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  const trimmed = reason.trim()
  const fieldError =
    (error instanceof ApiError ? error.fields?.reason : undefined) ??
    (touched && !trimmed ? 'دلیل مسدودسازی را بنویسید' : undefined)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (trimmed) onSubmit(trimmed)
  }

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="m-auto w-full max-w-lg rounded-2xl border border-line bg-card p-6 text-strong backdrop:bg-black/40"
    >
      <form onSubmit={submit}>
        <h2 className="mb-2 text-lg font-bold">مسدودکردن مشتری</h2>
        <p className="mb-4 text-sm text-muted">
          مشتری دیگر نمی‌تواند وارد شود و همه نشست‌هایش بسته می‌شوند. دلیل در رویدادها ثبت می‌شود.
        </p>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">دلیل</span>
          <textarea
            autoFocus
            rows={4}
            maxLength={REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={fieldError ? true : undefined}
            className={`w-full rounded-xl border bg-card px-3 py-2 text-strong outline-none transition-colors focus:border-brand-500 ${
              fieldError ? 'border-red-500' : 'border-line'
            }`}
          />
          <span className="mt-1 flex justify-between text-xs">
            <span className="text-red-600 dark:text-red-400">{fieldError}</span>
            <span className="text-muted">
              {toPersianDigits(reason.length)} / {toPersianDigits(REASON_MAX)}
            </span>
          </span>
        </label>
        {error && !fieldError ? (
          <div className="mt-3">
            <ErrorNote>{messageOf(error)}</ErrorNote>
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => ref.current?.close()}>
            انصراف
          </Button>
          <Button type="submit" variant="danger" loading={pending}>
            مسدودکردن
          </Button>
        </div>
      </form>
    </dialog>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="mb-1 text-xs text-muted">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  )
}

/** A titled table of the latest few; says so when there are more than it shows. */
function Section({
  title,
  shown,
  total,
  empty,
  children,
}: {
  title: string
  shown: number
  total: number
  empty: string
  children: ReactNode
}) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 flex items-baseline gap-2 font-semibold">
        {title}
        <span className="text-sm font-normal text-muted">
          {total > shown
            ? `${toPersianDigits(shown)} مورد آخر از ${toPersianDigits(total)}`
            : `${toPersianDigits(total)} مورد`}
        </span>
      </h2>
      <Card className="overflow-x-auto p-0">
        {total === 0 ? <p className="p-5 text-sm text-muted">{empty}</p> : children}
      </Card>
    </section>
  )
}

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-line text-right text-muted">
        <tr>
          {head.map((label) => (
            <th key={label} className="px-4 py-3 font-medium">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}
